import { getUID } from '@redviking/argonaut-util/src/getUID';
import { GetProcessByIdForProcessCollectNodeDocument } from 'types/db';
import { ProcessDataElementCollectionKindEnum, ProcessDataElementKindEnum } from '@redviking/argonaut-util/types/db-enums/process';
import type { LatestProcessCollectStandardizedOutput, NodeCfg, NodeControlSocketKindMap, NodeDataSocketKindMap, NodeSocketKind, NodeSocketKindMap } from '@redviking/argonaut-util/types/mes/applet-designs/flow.latest';
import { defineControlOpts } from '../../controls/FlowNodeControl';
import type { NodeSpec } from './NodeSpec';
import type { FlowNodeInput } from '../../sockets/FlowNodeInput';
import type { FlowNodeOutput } from '../../sockets/FlowNodeOutput';
import { gqlClient } from 'src/util/gql-client';
import { defaultControlUpdateValueHandler } from '@redviking/argonaut-core-ui/src/applet/design/flows/controls/control-event-handler';

const deletableOutputHelper = <S extends NodeSocketKind>(payload: {
    label: string;
    value: keyof NodeSocketKindMap['processCollect']['outputs'];
    nodeCfg: NodeCfg<'processCollect'>;
    outputType: S;
}): FlowNodeOutput.Opts<S, null> => {
    return {
        socket: payload.outputType,
        controlOpts: defineControlOpts({
            cfg: {
                rightAlign: true,
                label: payload.label,
                kind: 'deletableSocket',
            },
            enableWithConnectedSocket: true,
            on: {
                'update:value': () => {}, // no-op, this control does not use the update:value event
                'delete': (_, { flowEventEmitter }) => flowEventEmitter.emit('removeNodeSocket', {
                    direction: 'output',
                    removalMethod: {
                        kind: 'uniqueIdentifier',
                        value: payload.value,
                    },
                    newNodeCfg: {
                        ...payload.nodeCfg,
                        controlValues: {
                            ...payload.nodeCfg.controlValues,
                            useStandardizedOutputs: {
                                ...payload.nodeCfg.controlValues.useStandardizedOutputs,
                                [payload.value]: false,
                            },
                        },
                    },
                }),
            },
            value: null,
        }),
    };
};

const processCollectColumnLabels = {
    status: 'Status',
    badCount: 'Bad Count',
    goodCount: 'Good Count',
    validationError: 'Validation Error',
} satisfies {
    [key in keyof LatestProcessCollectStandardizedOutput]: string;
};

const processCollectColumnSelectionMap = Object.entries(processCollectColumnLabels).map(([ key, value ]) => ({
    label: value,
    value: key,
}));

export const processCollectUiNodeSpec: NodeSpec<NodeCfg<'processCollect'>> = {
    nodeCategory: 'argonautApi',
    requiredSockets: [ 'materialId' ],
    kindLabel: 'Process Collect',
    controls: nodeCfg => {
        const ret: ReturnType<NodeSpec<NodeCfg<'processCollect'>>['controls']> = {
            processId: defineControlOpts({
                value: nodeCfg.controlValues.processId,
                cfg: {
                    kind: 'selectProcess',
                    label: 'Process',
                },
                on: {
                    'update:value': async (processId, { flowEventEmitter }) => {
                        const {
                            process_process_by_pk,
                        } = await gqlClient.request({
                            document: GetProcessByIdForProcessCollectNodeDocument,
                            variables: {
                                processId,
                            },
                        });
                        if (!process_process_by_pk) {
                            throw new Error(`Process with ID ${processId} not found.`);
                        }
                        const dataElements: NodeCfg<'processCollect'>['controlValues']['dataElements'] = [];
                        for (const dataElement of process_process_by_pk.data_elements) {
                            if (dataElement.collection_kind === ProcessDataElementCollectionKindEnum.Collect) {
                                if (dataElement.kind === ProcessDataElementKindEnum.MaterialClass || dataElement.kind === ProcessDataElementKindEnum.MaterialModel) {
                                    // no-op collect items cannot be material class or model
                                } else {
                                    dataElements.push({
                                        id: dataElement.id,
                                        name: dataElement.name,
                                        type: dataElement.kind,
                                    });
                                }
                            }
                        }

                        flowEventEmitter.emit('updateNodeCfg', {
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...nodeCfg.controlValues,
                                    processId,
                                    dataElements,
                                },
                            },
                        });
                    },
                },
            }),
            location: defineControlOpts({
                value: nodeCfg.controlValues.location,
                cfg: {
                    kind: 'location',
                },
                on: { 'update:value': defaultControlUpdateValueHandler },
            }),
            useStandardizedOutputs: defineControlOpts({
                value: nodeCfg.controlValues.useStandardizedOutputs,
                cfg: {
                    selectLabel: 'Add Output',
                    kind: 'selectWithCallback',
                    items: () => {
                        const notUsedOutputs = Object.entries(nodeCfg.controlValues.useStandardizedOutputs).filter(([ , value ]) => !value).map(([ key ]) => key);
                        return processCollectColumnSelectionMap.filter(item => notUsedOutputs.includes(item.value));
                    },
                },
                on: {
                    'update:value': () => {}, // no-op, this control handles updates via the select callback
                    select (value, { flowEventEmitter }) {
                        const typedValue = value as keyof typeof nodeCfg.controlValues.useStandardizedOutputs;
                        flowEventEmitter.emit('updateNodeCfg', {
                            newNodeCfg: {
                                ...nodeCfg,
                                controlValues: {
                                    ...nodeCfg.controlValues,
                                    useStandardizedOutputs: {
                                        ...nodeCfg.controlValues.useStandardizedOutputs,
                                        [typedValue]: true,
                                    },
                                },
                            },
                        });
                    },
                },
            }),
                    
        };
        return ret;
    },
    cfg: () => {
        const processCollectNodeCfg: NodeCfg<'processCollect'> = {
            disabled: false,
            id: getUID(),
            kind: 'processCollect',
            width: 210,
            controlValues: {
                processId: '',
                dataElements: [],
                location: {
                    id: '',
                },
                useStandardizedOutputs: {
                    status: true,
                    badCount: true,
                    goodCount: true,
                    validationError: true,
                },
            },
        };
        return processCollectNodeCfg;
    },
    inputs: nodeCfg => {
        const controlInputs: {
            [k in keyof NodeControlSocketKindMap['processCollect']['inputs']]: FlowNodeInput.Opts<NodeControlSocketKindMap['processCollect']['inputs'][k]>;
        } = {
            collect: { socket: 'event', label: 'Collect Trigger' },
        };
        const dataInputs: {
            [k in keyof NodeDataSocketKindMap['processCollect']['inputs']]: FlowNodeInput.Opts<NodeDataSocketKindMap['processCollect']['inputs'][k]>;
        } = {
            materialId: { socket: 'string', label: 'Material ID' },
        };
        for (const de of nodeCfg.controlValues.dataElements) {
            if (de.type === ProcessDataElementKindEnum.String || de.type === ProcessDataElementKindEnum.Material) {
                // FUTURE: if we add more types like uuid or even more specific material uuid then we can handle that here
                dataInputs[`de_${de.id}`] = { socket: 'string', label: de.name };
            } else if (de.type === ProcessDataElementKindEnum.Number) {
                dataInputs[`de_${de.id}`] = { socket: 'number', label: de.name };
            } else if (de.type === ProcessDataElementKindEnum.Boolean) {
                dataInputs[`de_${de.id}`] = { socket: 'boolean', label: de.name };
            }
        }
        const ret: ReturnType<NodeSpec<NodeCfg<'processCollect'>>['inputs']> = {
            ...controlInputs,
            ...dataInputs,
        };
        return ret;
    },
    outputs: nodeCfg => {
        const controlOutputs: {
            [k in keyof NodeControlSocketKindMap['processCollect']['outputs']]: FlowNodeOutput.Opts<NodeControlSocketKindMap['processCollect']['outputs'][k]>;
        } = {
            badCollect: { socket: 'event', label: 'Collection Failed' },
            goodCollect: { socket: 'event', label: 'Collection Passed' },
        };
        const dataOutputs: {
            [k in keyof NodeDataSocketKindMap['processCollect']['outputs']]: FlowNodeOutput.Opts<NodeDataSocketKindMap['processCollect']['outputs'][k]>;
        } = {};

        if (nodeCfg.controlValues.useStandardizedOutputs.status) {
            dataOutputs.output_status = deletableOutputHelper({
                nodeCfg,
                label: 'Status',
                value: 'output_status',
                outputType: 'string',
            });
        }
        if (nodeCfg.controlValues.useStandardizedOutputs.badCount) {
            dataOutputs.output_badCount = deletableOutputHelper({
                nodeCfg,
                value: 'output_badCount',
                label: 'Bad Count',
                outputType: 'number',
            });
        }
        if (nodeCfg.controlValues.useStandardizedOutputs.goodCount) {
            dataOutputs.output_goodCount = deletableOutputHelper({
                nodeCfg,
                value: 'output_goodCount',
                label: 'Good Count',
                outputType: 'number',
            });
        }
        if (nodeCfg.controlValues.useStandardizedOutputs.validationError) {
            dataOutputs.output_validationError = deletableOutputHelper({
                nodeCfg,
                outputType: 'string',
                label: 'Validation Error',
                value: 'output_validationError',
            });
        }

        const ret: ReturnType<NodeSpec<NodeCfg<'processCollect'>>['outputs']> = {
            ...controlOutputs,
            ...dataOutputs,
        };
        return ret;
    },
    validator: node => {
        const errors: Record<string, string> = {};

        if (!node.controlValues.processId) {
            errors.processId = 'Process is required';
        }

        if ('deployParamKind' in node.controlValues.location) {
            if (!node.controlValues.location.deployParamId) {
                errors.location = 'Location deployment parameter is required';
            }
        } else if (!node.controlValues.location.id) {
            errors.location = 'Location is required';
        }

        return Object.keys(errors).length === 0 ? true : errors;
    },
};
